<template>
  <div :class="rootClass">
    <span ref="anchorRef" class="inline-flex">
      <slot />
    </span>
    <Popover ref="popoverRef" append-to="body" @hide="onPopoverHide">
      <div
        class="flex items-stretch overflow-hidden rounded border border-gpx_black-500 bg-white text-[13px] shadow-md"
      >
        <div class="flex items-center px-[8px] py-[4px]">{{ text }}</div>
        <button
          type="button"
          class="border-x border-gpx_black-500 px-[9px] py-[5px]"
          @click="onConfirmClick"
        >
          <CheckIcon class="size-[14px]" />
        </button>
        <button type="button" class="px-[9px] py-[5px]" @click="onCancelClick">
          <XMarkIcon class="size-[14px]" />
        </button>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { CheckIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import Popover from 'primevue/popover';
import { computed, nextTick, ref, watch } from 'vue';

const emit = defineEmits<{
  onConfirm: [];
  onCancel: [];
}>();

const props = withDefaults(
  defineProps<{
    text?: string;
    onConfirmText?: string;
    onCancelText?: string;
    /** When false, the confirmation popover does not open. */
    disabled?: boolean;
    /** Controlled visibility (e.g. after close click with unsaved changes). */
    shown?: boolean;
    class?: string;
  }>(),
  {
    text: 'Are you sure',
    onConfirmText: 'Yes',
    onCancelText: 'No',
    disabled: false,
    shown: false,
  },
);

const anchorRef = ref<HTMLElement | null>(null);
const popoverRef = ref<InstanceType<typeof Popover> | null>(null);

/** Avoid emitting onCancel when we hide programmatically to sync with `shown`. */
let suppressHideEmit = false;

function onPopoverHide() {
  if (suppressHideEmit) {
    return;
  }
  emit('onCancel');
}

function onConfirmClick() {
  suppressHideEmit = true;
  popoverRef.value?.hide();
  nextTick(() => {
    suppressHideEmit = false;
  });
  emit('onConfirm');
}

function onCancelClick() {
  suppressHideEmit = true;
  popoverRef.value?.hide();
  nextTick(() => {
    suppressHideEmit = false;
  });
  emit('onCancel');
}

const rootClass = computed(() => props.class);

watch(
  () => [props.shown, props.disabled] as const,
  ([shown, disabled]) => {
    nextTick(() => {
      if (shown && !disabled && anchorRef.value) {
        const el = anchorRef.value;
        popoverRef.value?.show({ currentTarget: el } as unknown as Event, el);
        return;
      }
      suppressHideEmit = true;
      popoverRef.value?.hide();
      nextTick(() => {
        suppressHideEmit = false;
      });
    });
  },
  { flush: 'post' },
);
</script>
